import { useState, useRef } from "preact/compat"
import { uploadContract, analyzeContract, ContractDocument } from "@/api/contract"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Upload, FileText, Loader2 } from "lucide-react"

export type ContractKind = "marketing" | "freight" | "purchasing"

interface ContractUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: string
  integrationId?: string
  onSuccess?: (contract: ContractDocument) => void
}

export function ContractUploadModal({
  open,
  onOpenChange,
  source,
  integrationId,
  onSuccess,
}: ContractUploadModalProps) {
  const [kind, setKind] = useState<ContractKind>("marketing")
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFileChange(e: Event) {
    const input = e.target as HTMLInputElement
    if (input.files && input.files[0]) {
      setFile(input.files[0])
      setError(null)
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile.type === "application/pdf") {
        setFile(droppedFile)
        setError(null)
      } else {
        setError("Please upload a PDF file")
      }
    }
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  async function handleSubmit() {
    if (!file) {
      setError("Please select a file")
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Upload the contract
      const contract = await uploadContract(file, {
        kind,
        source,
        integration_id: integrationId,
      })

      // Trigger analysis
      const analyzedContract = await analyzeContract(contract.id)

      onSuccess?.(analyzedContract)
      onOpenChange(false)
      resetForm()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to upload contract")
    } finally {
      setUploading(false)
    }
  }

  function resetForm() {
    setKind("marketing")
    setFile(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  function handleClose(open: boolean) {
    if (!open) {
      resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Contract</DialogTitle>
          <DialogDescription>
            Upload a contract document for {source.toUpperCase()}. The document will be analyzed to extract key terms.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4 py-4">
          {/* Contract Kind Toggle */}
          <div class="space-y-2">
            <label class="text-sm font-medium">Contract Type</label>
            <ToggleGroup
              type="single"
              value={kind}
              onValueChange={(value: string) => value && setKind(value as ContractKind)}
              class="justify-start"
            >
              <ToggleGroupItem value="marketing" aria-label="Marketing">
                Marketing
              </ToggleGroupItem>
              <ToggleGroupItem value="freight" aria-label="Freight">
                Freight
              </ToggleGroupItem>
              <ToggleGroupItem value="purchasing" aria-label="Purchasing">
                Purchasing
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* File Dropzone */}
          <div class="space-y-2">
            <label class="text-sm font-medium">Contract Document (PDF)</label>
            <div
              class={`
                border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                transition-colors hover:border-primary/50
                ${file ? "border-green-500 bg-green-50" : "border-gray-300"}
              `}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                class="hidden"
              />
              {file ? (
                <div class="flex items-center justify-center gap-2 text-green-700">
                  <FileText className="h-6 w-6" />
                  <span class="text-sm font-medium">{file.name}</span>
                </div>
              ) : (
                <div class="flex flex-col items-center gap-2 text-gray-500">
                  <Upload className="h-8 w-8" />
                  <p class="text-sm">
                    Drag and drop a PDF file, or click to browse
                  </p>
                </div>
              )}
            </div>
          </div>

          {error && (
            <p class="text-sm text-red-500">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || uploading}>
            {uploading ? (
              <>
                {/* Lucide only merges `className`; spin on a wrapper so it actually animates. */}
                <span class="mr-2 inline-flex shrink-0 motion-safe:animate-spin" aria-hidden>
                  <Loader2 className="h-4 w-4" />
                </span>
                Uploading...
              </>
            ) : (
              "Upload & Analyze"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
